import { useCallback, useMemo } from 'react';
import { TableCell, TableRow } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconDelete } from 'src/assets/icons/Delete';
import { EditIcon } from 'src/assets/icons/EditIcon';
import { IconFolderMove } from 'src/assets/icons/FolderMove';
import { IconGeneralText } from 'src/assets/icons/generationTools/IconGeneralText';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolsIcons } from 'src/components/GenerationToolsIcons';
import { ProjectActionsDropdown, ProjectDescription } from 'src/components/ProjectListItem';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { GENERATION_TOOL } from 'src/data/generationTool';
import {
  getProjectTypeLabel,
  isDocumentProjectType,
  TABLE_PROJECT_ACTIONS,
} from 'src/data/project';

import { getProjectAssetTools } from '../../lib/utils';
import { FavoriteProjectIndicator } from '../FavoriteProjectIndicator';

import { useStyles } from './useStyles';

// Both tools write whatever they are asked for, so their own icons say nothing about what the
// project holds — the column shows plain text for them instead.
const TOOL_ICON_OVERRIDES = {
  [GENERATION_TOOL.freestyle]: IconGeneralText,
  [GENERATION_TOOL.basicContentImprover]: IconGeneralText,
};

export const ProjectTableRows = ({ projects, onProjectAction, hideActions, nameOnly }) => {
  const styles = useStyles();

  const projectActions = useMemo(
    () => [
      {
        label: 'Rename',
        onClick: (event, project) => onProjectAction(TABLE_PROJECT_ACTIONS.rename, project),
        icon: <EditIcon />,
      },
      {
        label: 'Move to folder',
        onClick: (event, project) => onProjectAction(TABLE_PROJECT_ACTIONS.move, project),
        icon: <IconFolderMove />,
      },
      {
        label: 'Delete',
        onClick: (event, project) => onProjectAction(TABLE_PROJECT_ACTIONS.delete, project),
        icon: <IconDelete />,
      },
    ],
    [onProjectAction]
  );

  const handleRowClick = useCallback(
    (event, project) => {
      event.preventDefault();
      event.stopPropagation();
      onProjectAction(TABLE_PROJECT_ACTIONS.click, project);
    },
    [onProjectAction]
  );

  return projects.map((project) => {
    const toolsVisible = isDocumentProjectType(project.type);

    return (
      <TableRow
        key={project.id}
        className={styles.root}
        onClick={(event) => handleRowClick(event, project)}
        data-testid={`projectRow_${project.id}`}
      >
        {!hideActions && (
          <TableCell
            className={cx(styles.cell, styles.cellFavorite)}
            onClick={(e) => e.stopPropagation()}
          >
            <FavoriteProjectIndicator
              project={project}
              onFavoriteClick={() => onProjectAction(TABLE_PROJECT_ACTIONS.favorite, project)}
              className={cx(!project.favorite && styles.hiddenIcon, styles.favoriteIcon)}
            />
          </TableCell>
        )}

        <TableCell className={cx(styles.cell, styles.cellName)}>
          <div className={styles.contentName}>
            <TruncatedTypography variant={TYPOGRAPHY_VARIANT.regular14}>
              {project.name}
            </TruncatedTypography>
          </div>
        </TableCell>

        {!nameOnly && (
          <TableCell className={cx(styles.cell, styles.cellType)}>
            {toolsVisible ? (
              <GenerationToolsIcons
                tools={getProjectAssetTools(project)}
                iconOverrides={TOOL_ICON_OVERRIDES}
                maxVisible={4}
                groupByResult
              />
            ) : (
              <CustomTypography variant={TYPOGRAPHY_VARIANT.paragraph14}>
                {getProjectTypeLabel(project.type)}
              </CustomTypography>
            )}
          </TableCell>
        )}

        {!nameOnly && (
          <TableCell className={cx(styles.cell, styles.cellDescription)}>
            <ProjectDescription
              project={project}
              diffNow
            />
          </TableCell>
        )}

        {!hideActions && (
          <TableCell className={cx(styles.cell, styles.cellMenu)}>
            <ProjectActionsDropdown
              actions={projectActions}
              project={project}
              className={styles.hiddenIcon}
            />
          </TableCell>
        )}
      </TableRow>
    );
  });
};

ProjectTableRows.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  onProjectAction: PropTypes.func.isRequired,
  hideActions: PropTypes.bool,
  nameOnly: PropTypes.bool,
};

ProjectTableRows.defaultProps = {
  hideActions: false,
  nameOnly: false,
};
